/* eslint-disable prettier/prettier */
import { FunctionComponent, useEffect, useState } from "react";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { InputText } from "primereact/inputtext";
import { InputSwitch } from "primereact/inputswitch";
import { Sidebar } from "primereact/sidebar";
import * as XLSX from "xlsx";
import CountyService from "services/county-service";
import { setToaster } from "common/toastStore";

interface CountyRow {
  id: number;
  name: string;
  code: string;
  state: string;
  stateCode: string;
  isActive: boolean;
  createdDate: string;
  createdBy: string;
  updatedDate: string | null;
  updatedBy: string | null;
}

interface CountyForm {
  id: number | null;
  name: string;
  code: string;
  state: string;
  stateCode: string;
  isActive: boolean;
}

interface CountyFilters {
  name: string;
  code: string;
  state: string;
  stateCode: string;
}

const DEFAULT_FORM: CountyForm = {
  id: null,
  name: "",
  code: "",
  state: "",
  stateCode: "",
  isActive: true,
};

const DEFAULT_FILTERS: CountyFilters = {
  name: "",
  code: "",
  state: "",
  stateCode: "",
};

const normalizeText = (value: string) => value.trim().toLowerCase();

const mapRow = (row: any): CountyRow => ({
  id: Number(row?.id ?? row?.Id ?? 0),
  name: row?.name ?? row?.Name ?? "",
  code: row?.code ?? row?.Code ?? "",
  state: row?.state ?? row?.State ?? "",
  stateCode: row?.stateCode ?? row?.StateCode ?? "",
  isActive: Boolean(row?.isActive ?? row?.IsActive ?? false),
  createdDate: row?.createdDate ?? row?.CreatedDate ?? "",
  createdBy: row?.createdBy ?? row?.CreatedBy ?? "",
  updatedDate: row?.updatedDate ?? row?.UpdatedDate ?? null,
  updatedBy: row?.updatedBy ?? row?.UpdatedBy ?? null,
});

const ManageCounty: FunctionComponent = () => {
  const [loading, setLoading] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [rows, setRows] = useState<CountyRow[]>([]);
  const [filters, setFilters] = useState<CountyFilters>(DEFAULT_FILTERS);
  const [appliedFilters, setAppliedFilters] =
    useState<CountyFilters>(DEFAULT_FILTERS);
  const [filtersVisible, setFiltersVisible] = useState<boolean>(false);
  const [addVisible, setAddVisible] = useState<boolean>(false);
  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const [form, setForm] = useState<CountyForm>(DEFAULT_FORM);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const loadCounties = (appliedFilters: CountyFilters = DEFAULT_FILTERS) => {
    setLoading(true);
    CountyService.getCounties(appliedFilters)
      .then((response: any) => {
        const data = Array.isArray(response?.data) ? response.data : [];
        setRows(data.map(mapRow));
      })
      .catch((error: Error) => {
        console.log(error);
        setToaster({
          severity: "error",
          summary: "Load failed",
          detail: "Unable to load county records.",
        });
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadCounties(appliedFilters);
  }, [appliedFilters]);

  const applyFilters = () => {
    setAppliedFilters({ ...filters });
    setFiltersVisible(false);
  };

  const clearFilters = () => {
    setFilters(DEFAULT_FILTERS);
    setAppliedFilters(DEFAULT_FILTERS);
    setFiltersVisible(false);
  };

  const openAddDrawer = () => {
    setIsEditMode(false);
    setForm(DEFAULT_FORM);
    setErrors({});
    setAddVisible(true);
  };

  const openEditDrawer = (rowData: CountyRow) => {
    setIsEditMode(true);
    setForm({
      id: rowData.id,
      name: rowData.name,
      code: rowData.code,
      state: rowData.state,
      stateCode: rowData.stateCode,
      isActive: rowData.isActive,
    });
    setErrors({});
    setAddVisible(true);
  };

  const closeDrawer = () => {
    setAddVisible(false);
    setIsEditMode(false);
    setForm(DEFAULT_FORM);
    setErrors({});
  };

  const hasDuplicateCode = () => {
    const normalizedCode = normalizeText(form.code);
    const normalizedStateCode = normalizeText(form.stateCode);
    if (!normalizedCode) return false;

    return rows.some((row) => {
      if (row.id === form.id) return false;
      return (
        normalizeText(row.code) === normalizedCode &&
        normalizeText(row.stateCode) === normalizedStateCode
      );
    });
  };

  const validateForm = () => {
    const nextErrors: Record<string, string> = {};

    if (!form.name.trim()) nextErrors.name = "Name is required.";
    if (!form.code.trim()) nextErrors.code = "Code is required.";
    if (!form.state.trim()) nextErrors.state = "State is required.";
    if (!form.stateCode.trim()) nextErrors.stateCode = "State Code is required.";

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    if (hasDuplicateCode()) {
      setToaster({
        severity: "warn",
        summary: "Duplicate county",
        detail: "A county with the same Code and State Code already exists.",
      });
      return;
    }

    const payload = {
      Id: isEditMode ? form.id : null,
      Name: form.name.trim(),
      Code: form.code.trim(),
      State: form.state.trim(),
      StateCode: form.stateCode.trim(),
      IsActive: form.isActive,
    };

    setSaving(true);

    try {
      if (isEditMode) {
        await CountyService.updateCounty(payload);
      } else {
        await CountyService.createCounty(payload);
      }

      setToaster({
        severity: "success",
        summary: isEditMode ? "Updated" : "Created",
        detail: `County record ${isEditMode ? "updated" : "created"} successfully.`,
      });
      closeDrawer();
      loadCounties(filters);
    } catch (error: any) {
      console.log(error);
      setToaster({
        severity: "error",
        summary: isEditMode ? "Update failed" : "Create failed",
        detail:
          error?.response?.data ||
          `Unable to ${isEditMode ? "update" : "create"} county record.`,
      });
    } finally {
      setSaving(false);
    }
  };

  const renderActions = (rowData: CountyRow) => {
    return (
      <Button
        label="Edit"
        icon="pi pi-pencil"
        className="p-button-outlined p-button-sm"
        onClick={() => openEditDrawer(rowData)}
      />
    );
  };

  const exportToExcel = () => {
    if (!rows.length) {
      setToaster({
        severity: "info",
        summary: "No data",
        detail: "No records to export.",
      });
      return;
    }

    const exportRows = rows.map((row) => ({
      Name: row.name,
      Code: row.code,
      State: row.state,
      "State Code": row.stateCode,
    }));

    const ws = XLSX.utils.json_to_sheet(exportRows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Counties");
    XLSX.writeFile(wb, `manage_county_export_${new Date().toISOString()}.xlsx`);
  };

  return (
    <div className="p-4">
      <div className="flex justify-between items-center mb-3">
        <div>
          <h2 className="text-xl font-bold">Manage County</h2>
        </div>

        <div className="gap-3 flex flex-row">
          <Button
            icon="pi pi-plus"
            label="Add New"
            onClick={openAddDrawer}
            className="p-button-sm p-button-outlined p-button-success"
          />
          <Button
            icon="pi pi-filter"
            label="Filters"
            onClick={() => setFiltersVisible(true)}
            className="p-button-sm p-button-outlined p-button-warning"
          />
          <Button
            icon="pi pi-times"
            label="Clear"
            onClick={clearFilters}
            className="p-button-sm p-button-outlined"
          />
          <Button
            icon="pi pi-file-excel"
            label="Export to Excel"
            onClick={exportToExcel}
            className="p-button-sm p-button-outlined"
          />
        </div>
      </div>

      <DataTable
        className="shadow-md"
        value={rows}
        loading={loading}
        stripedRows
        paginator
        rows={10}
        rowsPerPageOptions={[10, 25, 50]}
        responsiveLayout="scroll"
        emptyMessage="No county records found."
        dataKey="id"
      >
        <Column field="name" header="Name" sortable />
        <Column field="code" header="Code" sortable />
        <Column field="state" header="State" sortable />
        <Column field="stateCode" header="State Code" sortable />
        <Column body={renderActions} header="Actions" style={{ width: "10rem" }} />
      </DataTable>

      <Sidebar
        visible={filtersVisible}
        position="right"
        onHide={() => setFiltersVisible(false)}
        style={{ width: 420 }}
      >
        <div className="grid">
          <div className="col-12">
            <h3 className="text-xl font-semibold mb-3">Filters</h3>
          </div>

          <div className="col-12">
            <label className="block text-sm mb-1">Name</label>
            <InputText
              value={filters.name}
              onChange={(e) =>
                setFilters((prev) => ({ ...prev, name: e.target.value }))
              }
              placeholder="Search by name"
              className="w-full"
            />
          </div>

          <div className="col-12">
            <label className="block text-sm mb-1">Code</label>
            <InputText
              value={filters.code}
              onChange={(e) =>
                setFilters((prev) => ({ ...prev, code: e.target.value }))
              }
              placeholder="Search by code"
              className="w-full"
            />
          </div>

          <div className="col-12">
            <label className="block text-sm mb-1">State</label>
            <InputText
              value={filters.state}
              onChange={(e) =>
                setFilters((prev) => ({ ...prev, state: e.target.value }))
              }
              placeholder="Search by state"
              className="w-full"
            />
          </div>

          <div className="col-12">
            <label className="block text-sm mb-1">State Code</label>
            <InputText
              value={filters.stateCode}
              onChange={(e) =>
                setFilters((prev) => ({ ...prev, stateCode: e.target.value }))
              }
              placeholder="Search by state code"
              className="w-full"
            />
          </div>

          <div className="col-12 flex gap-2 mt-2">
            <Button label="Apply" icon="pi pi-filter" onClick={applyFilters} />
            <Button
              label="Clear"
              icon="pi pi-times"
              className="p-button-outlined"
              onClick={clearFilters}
            />
          </div>
        </div>
      </Sidebar>

      <Sidebar
        visible={addVisible}
        position="right"
        style={{ width: "38rem" }}
        onHide={closeDrawer}
      >
        <div className="flex flex-col gap-4">
          <div>
            <h3 className="m-0 text-lg font-semibold">
              {isEditMode ? "Edit County" : "Add County"}
            </h3>
            <p className="m-0 text-sm text-500">
              {isEditMode
                ? "Update the existing county details."
                : "Create a new county entry."}
            </p>
          </div>

          <div className="flex flex-col gap-2">
            <label htmlFor="county-name">Name</label>
            <InputText
              id="county-name"
              value={form.name}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, name: e.target.value }))
              }
              className={errors.name ? "p-invalid" : ""}
            />
            {errors.name && <small className="p-error">{errors.name}</small>}
          </div>

          <div className="flex flex-col gap-2">
            <label htmlFor="county-code">Code</label>
            <InputText
              id="county-code"
              value={form.code}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, code: e.target.value }))
              }
              className={errors.code ? "p-invalid" : ""}
            />
            {errors.code && <small className="p-error">{errors.code}</small>}
          </div>

          <div className="flex flex-col gap-2">
            <label htmlFor="county-state">State</label>
            <InputText
              id="county-state"
              value={form.state}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, state: e.target.value }))
              }
              className={errors.state ? "p-invalid" : ""}
            />
            {errors.state && <small className="p-error">{errors.state}</small>}
          </div>

          <div className="flex flex-col gap-2">
            <label htmlFor="county-state-code">State Code</label>
            <InputText
              id="county-state-code"
              value={form.stateCode}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, stateCode: e.target.value }))
              }
              className={errors.stateCode ? "p-invalid" : ""}
            />
            {errors.stateCode && (
              <small className="p-error">{errors.stateCode}</small>
            )}
          </div>

          <div className="flex items-center gap-2">
            <InputSwitch
              inputId="county-is-active"
              checked={form.isActive}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, isActive: Boolean(e.value) }))
              }
            />
            <label htmlFor="county-is-active">Active</label>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button
              label="Cancel"
              className="p-button-outlined"
              onClick={closeDrawer}
              disabled={saving}
            />
            <Button
              label={isEditMode ? "Update" : "Save"}
              icon={saving ? "pi pi-spin pi-spinner" : "pi pi-check"}
              onClick={handleSave}
              disabled={saving}
            />
          </div>
        </div>
      </Sidebar>
    </div>
  );
};

export default ManageCounty;
